import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getToken, getMe, login as apiLogin, logout as apiLogout, MeResponse } from "../api";

type AuthState = {
  loading: boolean;
  authed: boolean;
  me: MeResponse | null;
  username: string | null;
  isSuperAdmin: boolean;
  isGroupAdmin: boolean;   // admin of at least one group
  isAnyAdmin: boolean;     // super or group-admin
  mustChange: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
};

const Ctx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<MeResponse | null>(null);

  const load = useCallback(async () => {
    if (!getToken()) { setMe(null); setLoading(false); return; }
    try {
      const m = await getMe();
      setMe(m);
    } catch {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const login = useCallback(async (u: string, p: string) => {
    await apiLogin(u, p);
    const m = await getMe();
    setMe(m);
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    setMe(null);
  }, []);

  const value: AuthState = {
    loading,
    authed: !!me,
    me,
    username: me?.username ?? null,
    isSuperAdmin: !!me?.super_admin,
    isGroupAdmin: (me?.admin_of?.length ?? 0) > 0,
    isAnyAdmin: !!me?.super_admin || (me?.admin_of?.length ?? 0) > 0,
    mustChange: !!me?.must_change_password,
    login,
    logout,
    refresh: load,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used within <AuthProvider>");
  return v;
}
